import React from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Breadcrumbs } from '@duck/ui'
import { useTar } from '@/modules'
import { useAdminChallenge } from '@/hooks/useAdminChallenge'
import { StandardLoader } from '@/components/StandardLoader'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { UserBadge } from '@/components/UserBadge'
import { ChallengeActions } from './ChallengeActions'
import { ChallengeDetailCards } from './ChallengeDetailCards'
import { ChallengeContentTabs } from './ChallengeContentTabs'
import { JsonTree } from './JsonTree'

interface ChallengeDetailPageProps {
  slug: string
}

export const ChallengeDetailPage: React.FC<ChallengeDetailPageProps> = ({ slug }) => {
  const { t } = useTar()
  const navigate = useNavigate()
  const { challengeInfo, isLoading, isError, error, notFound, refetch } = useAdminChallenge(slug)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <StandardLoader />
      </div>
    )
  }

  if (isError) {
    const message =
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
      (error as Error)?.message ??
      t('common.anErrorOccurred')
    return (
      <div className="rounded-md bg-error/10 border border-error/20 p-4">
        <p className="text-error text-sm">{message}</p>
      </div>
    )
  }

  if (notFound || !challengeInfo) {
    return (
      <NoDataEmptyState
        title={t('challenges.detailNotFound')}
        buttonText={t('challenges.breadcrumbChallenges')}
        onButtonClick={() => { void navigate({ to: '/admin/challenges' }) }}
      />
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('challenges.breadcrumbAdmin'), href: '/admin' },
          { label: t('challenges.breadcrumbChallenges'), href: '/admin/challenges' },
          { label: challengeInfo.challenge.slug },
        ]}
        renderLink={(item, children) => (
          <Link to={item.href}>{children}</Link>
        )}
      />

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            {challengeInfo.task?.name}
          </h2>
          {challengeInfo.user ? (
            <UserBadge
              userId={challengeInfo.user.userId}
              fullName={challengeInfo.user.fullName}
              role={challengeInfo.user.role}
              className="mt-1 inline-block"
            />
          ) : (
            <p className="text-sm text-muted-foreground">{challengeInfo.challenge.userId}</p>
          )}
        </div>
        <ChallengeActions
          challengeInfo={challengeInfo}
          onChanged={() => { void refetch() }}
          onDeleted={() => { void navigate({ to: '/admin/challenges' }) }}
        />
      </div>

      <ChallengeDetailCards challengeInfo={challengeInfo} />

      {challengeInfo.challenge.workState && (
        <div className="bg-card border border-border rounded-md p-4">
          <h3 className="text-sm font-semibold text-foreground mb-2">
            {t('challenges.detailWorkState')}
          </h3>
          <JsonTree data={challengeInfo.challenge.workState} rootLabel="workState" />
        </div>
      )}

      <ChallengeContentTabs challengeInfo={challengeInfo} />
    </div>
  )
}
